import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { Hash, XCircle, Calendar, Activity, Slash } from 'react-feather'
import { t } from '@lingui/core/macro'

import { MeetingLinkOrder } from '@/core/graphql/types'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { useLocale } from '@/lib/next-shim'

import { getStatusLabel } from '../lib/orderLabels'
import { getOrderTimeline } from '../lib/orderTimeline'
import { OrderStatusTimeline } from './OrderStatusTimeline'

type OrderHeaderProps = {
  order: MeetingLinkOrder
  referenceId: string
  ordersSectionTitle: string
  cancelButtonLabel: string
  canCancel: boolean
  onCancel: () => void
  isCanceling: boolean
}

export function OrderHeader({ order, referenceId, ordersSectionTitle, cancelButtonLabel, canCancel, onCancel, isCanceling }: OrderHeaderProps) {
  const lang = useLocale()
  const { steps, cancelled } = getOrderTimeline(order.status)

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-primary/10 via-white to-white shadow-sm">
      <div className="flex flex-col gap-4 p-6 sm:p-8">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{order.meetingLink?.name || ordersSectionTitle}</h1>
              <StatusBadge status={order.status} statusText={getStatusLabel(order.status)} />
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Hash size={14} aria-hidden="true" /> {t`Reference`}: <span className="font-medium text-gray-700">{referenceId || '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar size={14} aria-hidden="true" /> {dayjs(order.startDate).locale(lang).format('ddd D MMM YYYY')}
              </span>
              {order.speciality && (
                <span className="flex items-center gap-1.5 capitalize">
                  <Activity size={14} aria-hidden="true" /> {order.speciality}
                </span>
              )}
            </div>
          </div>
          {canCancel && (
            <Button
              variant="ghost"
              className="shrink-0 text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={onCancel}
              disabled={isCanceling}
            >
              <XCircle size={18} className="mr-2" />
              {cancelButtonLabel}
            </Button>
          )}
        </div>

        {cancelled ? (
          <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 p-4">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <Slash size={14} aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-semibold text-red-700">{t`This request is no longer active`}</p>
              <p className="text-sm text-red-600">{t`It was cancelled or declined, so no consultation will be scheduled.`}</p>
            </div>
          </div>
        ) : (
          <div className="pt-2">
            <OrderStatusTimeline steps={steps} />
          </div>
        )}
      </div>
    </div>
  )
}
